// Performance Optimization: Lazy load heavy components to reduce initial bundle size
import React, { useState, useEffect, useRef, lazy, Suspense, useCallback, memo } from 'react'
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from 'swiper/modules';
import { Link } from 'react-router-dom';
import Header from './Header';
import Metatag from './Metatag';
import MountWhenNear from './MountWhenNear';
// Organization structured data comes from server/index.js (in the initial HTML), so it isn't repeated here
import { aubussiness, uacorporate, aufoundermessage, auvalues, autestimonial } from '../local-api/index';

// CSS imports
import "./mainpage.css"
import "./mainresponsive.css"
import 'bootstrap/dist/css/bootstrap.min.css';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';

// Lazy load heavy components that are below the fold
const ReactPlayer = lazy(() => import('react-player'));
const Footer = lazy(() => import('./Footer'));
const Getintouch = lazy(() => import('./Getintouch'));

// Performance: Memoized component to prevent unnecessary re-renders
function Mainpage() {
	// Remove unused state variables to reduce memory footprint
	const [videoplay, setVideoPlay] = useState(null);
	const [show, setShow] = useState(`${auvalues?.accordionImg[0]?.img}`);
	const [appendVideo, setAppendVideo] = useState(false);
	const elementRef = useRef(null)
	const bannerRef = useRef(null)

	// Performance: Memoize event handlers with useCallback to prevent re-creation on every render
	const handleVideoClick = useCallback(() => {
		setVideoPlay(true);
	}, []);

	const handleAccordionClick = useCallback((index) => {
		const imagesUrl = auvalues?.accordionImg.map((element) => element.img);
		setShow(imagesUrl[index]);
	}, []);

	// Performance: Intersection Observer for lazy loading videos when visible
	useEffect(() => {
		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting && !appendVideo) {
				setAppendVideo(true);
			}
		}, {
			threshold: 0.5,
		});
		const element = elementRef.current;
		if (element) {
			observer.observe(element);
		}
		return () => {
			if (element) {
				observer.unobserve(element);
			}
		};
	}, [appendVideo]);


	return (
		<React.Fragment>
			<Metatag
				link="/"
				title="Real Estate Investment in India: Delhi NCR Properties | AU"
				description="Explore lucrative real estate investment opportunities in Delhi NCR, India. Discover prime properties for investment with Aurea Real Estate."
			/>
			<Header />
			{/* Performance: Lazy load Getintouch component with Suspense */}
			<Suspense fallback={<div />}>
				<Getintouch />
			</Suspense>
			<main>
				<div className='aurealestate-banner' ref={bannerRef}>
					<h1 style={{ display: 'none', }}>AU Real Estate Projects</h1>
					<Swiper
						slidesPerView={1}
						spaceBetween={0}
						loop={false}
						autoplay={{
							delay: 8000
						}}
						modules={[Navigation, Autoplay]}
					>
						<SwiperSlide>
							<img
								src={'/images/Banner1.webp'}
								srcSet='/images/Banner1-800.webp 800w, /images/Banner1-1366.webp 1366w, /images/Banner1.webp 1914w'
								sizes='100vw'
								alt='AU Real Estate Banner'
								loading='eager'
								fetchpriority='high'
								width={1920}
								height={830}
								style={{ width: '100%', height: 'auto' }}
							/>
						</SwiperSlide>
						{/* Performance: First slide loads eagerly (LCP optimization), others lazy */}
						<SwiperSlide>
							<img
								src={'/images/Banner-8.webp'}
								srcSet='/images/Banner-8-800.webp 800w, /images/Banner-8-1366.webp 1366w, /images/Banner-8.webp 1600w'
								sizes='100vw'
								alt='AU Real Estate Banner'
								loading='lazy'
								width={1920}
								height={830}
								style={{ width: '100%', height: 'auto' }}
							/>
						</SwiperSlide>
						<SwiperSlide>
							<Link to="/family-hub">
								<img
									src={'/images/Banner-9.webp'}
									srcSet='/images/Banner-9-800.webp 800w, /images/Banner-9-1366.webp 1366w, /images/Banner-9.webp 1600w'
									sizes='100vw'
									alt='AU Real Estate Banner'
									loading='lazy'
									width={1920}
									height={830}
									style={{ width: '100%', height: 'auto' }}
								/>
							</Link>
						</SwiperSlide>
						<SwiperSlide>
							<Link to={'/cosmos-corner'}>
								<img
									src={'/images/Banner-10.webp'}
									srcSet='/images/Banner-10-800.webp 800w, /images/Banner-10-1366.webp 1366w, /images/Banner-10.webp 1600w'
									sizes='100vw'
									alt='AU Real Estate Banner'
									loading='lazy'
									width={1920}
									height={830}
									style={{ width: '100%', height: 'auto' }}
								/>
							</Link>
						</SwiperSlide>
						<SwiperSlide>
							<img
								src={'/images/Banner2.webp'}
								srcSet='/images/Banner2-800.webp 800w, /images/Banner2.webp 1400w'
								sizes='100vw'
								alt='AU Real Estate Projects'
								loading='lazy'
								width={1920}
								height={830}
								style={{ width: '100%', height: 'auto' }}
							/>
						</SwiperSlide>
						{/* <SwiperSlide>
						<img
							src={'/images/Banner6.webp'}
							srcSet='/images/Banner6-800.webp 800w, /images/Banner6-1366.webp 1366w, /images/Banner6.webp 1914w'
							sizes='100vw'
							alt='Real Estate Investment'
							loading='lazy'
							width={1920}
							height={830}
							style={{ width: '100%', height: 'auto' }}
						/>
					</SwiperSlide> */}
						{/* <SwiperSlide>
							<img
								src={'/images/Banner4.webp'}
								srcSet='/images/Banner4-800.webp 800w, /images/Banner4-1366.webp 1366w, /images/Banner4.webp 1914w'
								sizes='100vw'
								alt='Property Development'
								loading='lazy'
								width={1920}
								height={830}
								style={{ width: '100%', height: 'auto' }}
							/>
						</SwiperSlide> */}
						<SwiperSlide>
							<img
								src={'/images/Banner3.webp'}
								srcSet='/images/Banner3-800.webp 800w, /images/Banner3.webp 1400w'
								sizes='100vw'
								alt='Luxury Properties'
								loading='lazy'
								width={1920}
								height={830}
								style={{ width: '100%', height: 'auto' }}
							/>
						</SwiperSlide>
						<SwiperSlide>
							<Link to={'/the-sunflower'} target='_blank'>
								<img
									src={'/images/Banner-7.jpg'}
									srcSet='/images/Banner-7.jpg 800w, /images/Banner-7.jpg 1400w'
									sizes='100vw'
									alt='Luxury Properties'
									loading='lazy'
									width={1920}
									height={830}
									style={{ width: '100%', height: 'auto' }}
								/>
							</Link>
						</SwiperSlide>
					</Swiper>
				</div>
				<div className='aurealestate-auphilosopy'>
					<div className='aurealestate-auphilosopyline'>
					</div>
					<div className='container'>
						<div className='row'>
							<div className='col-lg-12'>
								<h2>AU Philosophy</h2>
								<p>AU Group's philosophy revolves around integrity, innovation, and unwavering dedication to clients. We believe in transparent practices, superior service, and fostering a sense of pride in ownership. Our commitment to these values defines our path towards excellence in the real estate industry.</p>
							</div>
						</div>
					</div>
					<div style={{ width: '100%' }}>
						{/* Performance: Above-fold image loads eagerly for better LCP */}
						<img
							src='/websiteimages/au-philosopy-img.webp'
							srcSet='/websiteimages/au-philosopy-img-800.webp 800w, /websiteimages/au-philosopy-img.webp 1600w'
							sizes='100vw'
							fetchpriority='high'
							alt='AU Philosophy'
							width='600'
							height='295'
							loading='eager'
						/>
					</div>
				</div>

				{/* Performance: Below-fold content with lazy-loaded images */}
				<div className='aurealestate-bussiness' >
					<div className='container'>
						<h2>{aubussiness?.heading}</h2>
						<div className='row'>
							<div className='col-lg-12'>
								<div className="aurealestate-bussinessimgmaintop">
									{aubussiness?.bussinessimgmaintop?.map((element, i) => {
										return <div key={i} className="aurealestate-bussinessimgdiv">
											{/* Performance: Lazy load below-fold images */}
											<img src={element?.src} alt={element?.alt} loading='lazy' width='555' height='300' />
											<div className="aurealestate-bussinesscontentdiv">
												<a href={element?.url} target='_blank' rel='noopener noreferrer'>
													<img className='logo' src={element?.logoSrc} alt={element?.logoAlt} loading='lazy' width='120' height='50' />
													<p>{element?.text}</p>
												</a>
											</div>
										</div>
									})}
								</div>
								<div className="aurealestate-bussinessimgmainbottom">
									{aubussiness?.bussinessimgmainbottom?.map((element, i) => {
										return <div key={i} className="aurealestate-bussinessimgdivbottom">
											<img src={element?.src} srcSet={element?.srcSmall ? `${element.srcSmall} 370w, ${element.src} 445w` : undefined} sizes='(max-width: 590px) 100vw, (max-width: 1399px) 367px, 426px' alt={element?.alt} loading='lazy' width='400' height='300' />
											<div className="aurealestate-bussinesscontentdivbottom">
												<Link to={element?.url} >
													<img className='logo' src={element?.logoSrc} alt={element?.logoAlt} loading='lazy' width='150' height='50' />
													<p>{element?.text}</p>
												</Link>
											</div>
										</div>
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='exceeding-expectations' >
					<h2>{uacorporate?.heading}</h2>
					<div className="video-div" >
						<div
							id="ytvideoclick"
							className="ytvideo"
							data-video={uacorporate?.dataVideo}
							style={{ backgroundImage: `url(${uacorporate?.backgroundImg})` }}
							onClick={handleVideoClick}
						>
							{
								videoplay == null ?
									<div className="wrapper">
										<div className="video-main">
											<div className="promo-video">
												<div className="waves-block">
													<div className="waves wave-1"></div>
													<div className="waves wave-2"></div>
													<div className="waves wave-3"></div>
												</div>
											</div>
											{/* Performance: Use button instead of anchor with javascript:void(0) */}
											<button
												type="button"
												className="video video-popup mfp-iframe"
												style={{ background: 'none', border: 'none', cursor: 'pointer' }}
												aria-label="Play video"
											>
												<i className="fa fa-play"></i>
											</button>
										</div>
									</div>
									: <iframe
										className="youtubevideo"
										src={uacorporate?.youTubeVidio}
										title="YouTube video player"
										frameBorder="0"
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
										allowFullScreen
									></iframe>
							}
						</div>
					</div>
				</div>
				<div ref={elementRef} className='foundermessage-main' >
					<div className='foundermessage-line'>
						<div className='container'>
							<div className='row'>
								<div className='col-lg-12'>
									<div className='foundermessage-div'>
										<div className='foundermessage-leftdiv'>
											<h3>{aufoundermessage?.heading}</h3>
											<p>{aufoundermessage?.text1}</p>
											<p>{aufoundermessage?.text2}</p>
											<h4 dangerouslySetInnerHTML={{ __html: aufoundermessage?.subHeading }}></h4>
										</div>
										<div className='foundermessage-rightdiv'>
											{/* Performance: Lazy load below-fold images */}
											<img
												src={aufoundermessage?.founderImg}
												srcSet={`${aufoundermessage?.founderImgSmall} 500w, ${aufoundermessage?.founderImg} 626w`}
												sizes='(max-width: 590px) 100vw, (max-width: 1024px) 50vw, 500px'
												alt={aufoundermessage?.imgAlt}
												loading='lazy'
												width='500'
												height='524'
												style={{ height: 'auto' }}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='auvalues-main' >
					<div className='auvalues-auvaluesline'>
					</div>
					<div className='container'>
						<div className='row'>
							<div className='col-lg-12'>
								<h3>{auvalues?.heading}</h3>
							</div>
							<div className='col-xl-6 col-lg-6'>
								{/* Performance: Lazy load accordion images */}
								<img
									src={show}
									className='mapmainclass object-fit-cover'
									alt={auvalues?.accordionImg[0]?.alt}
									loading='lazy'
									width='600'
									height='400'
								/>
							</div>
							<div className='col-xl-6 col-lg-6'>
								<div className="accordion" id="accordionExample">
									<div className="accordion-item" onClick={() => handleAccordionClick(0)}>
										<h2 className="accordion-header" id={auvalues?.accordionItem[0]?.uniqueHeadingId}>
											<button
												className="accordion-button"
												type="button"
												data-related-map={auvalues?.accordionItem[0]?.dataRelatedMap}
												data-bs-toggle="collapse"
												data-bs-target={`#${auvalues?.accordionItem[0]?.uniqueTabId}`}
												aria-expanded="true"
												aria-controls={auvalues?.accordionItem[0]?.uniqueTabId}
											>
												{auvalues?.accordionItem[0]?.heading}
											</button>
										</h2>
										<div
											id={auvalues?.accordionItem[0]?.uniqueTabId}
											className="accordion-collapse collapse show"
											aria-labelledby={auvalues?.accordionItem[0]?.uniqueHeadingId}
											data-bs-parent="#accordionExample"
										>
											<div className="accordion-body">
												<p dangerouslySetInnerHTML={{ __html: auvalues?.accordionItem[0]?.text }}></p>
											</div>
										</div>
									</div>
									{auvalues?.accordionItem?.map((element, i) => {
										if (i > 0) {
											return <div key={i} className="accordion-item" onClick={() => handleAccordionClick(i)}>
												<h2 className="accordion-header" id={element?.uniqueHeadingId}>
													<button
														className="accordion-button collapsed"
														type="button"
														data-related-map={element?.dataRelatedMap}
														data-bs-toggle="collapse"
														data-bs-target={`#${element?.uniqueTabId}`}
														aria-expanded="false"
														aria-controls={element?.uniqueTabId}
													>
														{element?.heading}
													</button>
												</h2>
												<div
													id={element?.uniqueTabId}
													className="accordion-collapse collapse"
													aria-labelledby={element?.uniqueHeadingId}
													data-bs-parent="#accordionExample"
												>
													<div className="accordion-body">
														<p dangerouslySetInnerHTML={{ __html: element?.text }}></p>
													</div>
												</div>
											</div>
										}
										return null;
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
				{/* Performance: Lazy load ReactPlayer only when testimonial section is visible */}
				<div className='testimonial-main'>
					{/* Below the fold: mount the sliders near the viewport so their layout reads don't force reflows on load */}
					<MountWhenNear minHeight={450}>
					<Swiper
						slidesPerView={1}
						spaceBetween={0}
						loop={true}
						centeredSlides={true}
						grabCursor={false}
						noSwiping={true}
						noSwipingClass={'swiper-no-swiping'}
						navigation={{
							nextEl: ".testimonials-swiper-button-next",
							prevEl: ".testimonials-swiper-button-prev",
							disabledClass: "swiper-button-disabled",
						}}
						modules={[Navigation]}
						className="testimonial-left"
					>
						{autestimonial?.map((element, i) => {
							return <SwiperSlide className="swiper-no-swiping" key={i}>
								{/* Performance: Lazy load ReactPlayer with Suspense fallback */}
								{appendVideo && (
									<Suspense fallback={<div style={{ width: '100%', height: '400px', background: '#f0f0f0' }} />}>
										<ReactPlayer className='themeplayone' url={element?.url} allowFullScreen />
									</Suspense>
								)}
							</SwiperSlide>
						})}
					</Swiper>
					<Swiper
						slidesPerView={1}
						spaceBetween={0}
						loop={true}
						centeredSlides={true}
						noSwiping={true}
						noSwipingClass={'swiper-no-swiping'}
						grabCursor={false}
						navigation={{
							nextEl: ".testimonials-swiper-button-next",
							prevEl: ".testimonials-swiper-button-prev",
							disabledClass: "swiper-button-disabled",
						}}
						modules={[Navigation]}
						className="testimonial-right"
					>
						{autestimonial?.length > 0 && autestimonial.map((element, i) => (
							<SwiperSlide className="swiper-no-swiping" key={i}>
								<p>{element?.text}</p>
								<div className='testimonial-rightone'>
									<h3 className='h4'>{element?.name}</h3>
								</div>
							</SwiperSlide>
						))}
						<div className='testimonials-swiper-button'>
							<div className="testimonials-swiper-button-prev">
								<svg xmlns="http://www.w3.org/2000/svg" height="45" viewBox="0 -960 960 960" width="45">
									<path d="M396.154-267.692 183.846-480l212.308-212.308 28.308 28.77L260.923-500h515.231v40H260.923l163.539 163.538-28.308 28.77Z" />
								</svg>
							</div>
							<div className="testimonials-swiper-button-next">
								<svg xmlns="http://www.w3.org/2000/svg" height="45" viewBox="0 -960 960 960" width="45">
									<path d="m547.692-267.692-28.307-28.77L682.923-460H200v-40h482.923L519.385-663.538l28.307-28.77L760-480 547.692-267.692Z" />
								</svg>
							</div>
						</div>
					</Swiper>
					</MountWhenNear>

				</div>
				<div className='inthenews-main' >
					<div className='container'>
						<div className='row'>
							<div className='col-lg-12'>
								<div className='inthenewsdivtop'>
									<h3>In the News</h3>
									<a href='/news'>View All <svg width="28" height="11" viewBox="0 0 28 11" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M22.6035 0.729126L26.9785 5.10413M26.9785 5.10413L22.6035 9.47913M26.9785 5.10413H0.728516" stroke="black" strokeWidth="1.45833" strokeLinecap="round" strokeLinejoin="round" />
									</svg></a>
								</div>
							</div>
						</div>
					</div>
					<div className='newsslider container'>

						<MountWhenNear minHeight={420}>
						<Swiper
							spaceBetween={20}
							loop={true}
							// centeredSlides={true}
							breakpoints={{
								0: {
									slidesPerView: 1,
								},
								768: {
									slidesPerView: 3,
								}
							}}
							className='offers'
						>
							<SwiperSlide>
								<div className='newsdiv'>
									<a rel="noreferrer" href='https://realty.economictimes.indiatimes.com/news/industry/survey-soon-circle-rates-in-noida-to-be-revised-after-three-years/102819803' target='_blank'>
										<img src='/websiteimages/news-img1.webp' alt='News' loading='lazy' />
										<p>Survey soon, circle rates in Noida to be revised after three years</p>
										<h4>Read  More</h4>
									</a>
								</div>
							</SwiperSlide>
							<SwiperSlide>
								<div className='newsdiv'>
									<a rel="noreferrer" href='https://realty.economictimes.indiatimes.com/news/rera/register-projects-with-rera-safeguard-interests-of-buyers-up-rera-chairman-to-promoters/102962641' target='_blank'>
										<img src='/websiteimages/news-img2.webp' alt='News' loading='lazy' />
										<p>Register projects with RERA, safeguard interests of buyers: UP-RERA chairman to promoters</p>
										<h4>Read  More</h4>
									</a>
								</div>
							</SwiperSlide>
							<SwiperSlide>
								<div className='newsdiv'>
									<a rel="noreferrer" href='https://realty.economictimes.indiatimes.com/news/industry/noida-may-allot-plots-via-draw-of-lots-interviews-not-e-auction/104291740' target='_blank'>
										<img src='/websiteimages/news-img3.webp' alt='News' loading='lazy' />
										<p>Noida may allot plots via draw of lots, interviews, not e-auction</p>
										<h4>Read  More</h4>
									</a>
								</div>
							</SwiperSlide>
						</Swiper>
						</MountWhenNear>
					</div>
				</div>
			</main>
			{/* Performance: Lazy load Footer component with Suspense */}
			<Suspense fallback={<div />}>
				<Footer />
			</Suspense>
		</React.Fragment>
	)
}

// Performance: Memoize component to prevent unnecessary re-renders
export default memo(Mainpage)